import { ActionButton, Paginator, Show, TableComponent } from 'components'
import { useAppNavigate, useAppSearch } from '_graphql/cache/auth'
import { useUrlState, withRoles } from 'utils'

import { Action } from 'components/buttons/action-button'
import ApproveOfferContainer from './approve'
import FilterMenu from './filter'
import RemoveContainer from 'pages/create-closing-kek/remove'
import ReopenContainer from 'pages/create-closing-kek/reopen'
import ViewNotesContainer from 'pages/create-closing-kek/view'
import _ from 'lodash'
import clsx from 'clsx'
import moment from 'moment'
import numeral from 'numeral'
import { status_type } from '_graphql/__generated__/globalTypes-kek'
import { useKEKOffers } from 'hooks/data/use-offers'
import { usePagination } from 'hooks'

type Props = {}

const UnapprovedOffers = (props: Props) => {
    const { search, limit, offset, setPage } = usePagination();
    const navigate = useAppNavigate();
    const searchParams = useAppSearch();
    const [modal, setModal] = useUrlState("modal");

    const { refetch, offers, loading, count } = useKEKOffers({
        args: {
            offset,
            limit,
            search,
            status: ["CLOSED"],
            claim_status: [],
            payment_status: [],
            approval_status: status_type.UNAPPROVED,
            insurer_id: searchParams?.insurer ? [searchParams.insurer] : undefined,
            class_of_business_id: searchParams.business ? [searchParams.business] : undefined,
            staff_id: searchParams.employee ? [searchParams.employee] : undefined,
            expiry_status: searchParams.expiry_status,
        }
    })



    const dispatchAction =
        (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
            () => {
                navigate({
                    search: (old) => ({
                        ...old,
                        id,
                        modal: action,
                    }),
                });
            };

    const expandItem = (id: string) => {
        navigate({
            to: `./${id}/credit-notes`,
        });
    };

    return (
        <>
            <TableComponent
                title={"Unapproved Offers"}
                hasSearch
                loading={loading}
                renderFilter={({ filterOpen, setFilterOpen }) => (
                    <FilterMenu
                        open={filterOpen}
                        setOpen={setFilterOpen}
                    />
                )}
                columns={[
                    {
                        name: "Policy #",
                        accessor: "offer_detail.policy_number",
                        render: (value) => (
                            <div className="flex flex-col items-start flex-wrap break-all break-words">
                                <div className="flex flex-row items-center gap-x-1">
                                    <span className="font-bold text-xs overflow-hidden text-ellipsis  ">
                                        {value.offer_detail?.policy_number || "N/A"} <Show if={Boolean(value.offer_retrocedent?.created_at)}>
                                            <span>
                                                - <span className='text-xs text-red-500 italic'>Retro</span>
                                            </span>
                                        </Show>
                                    </span>

                                </div>
                                <div
                                    className={clsx(
                                        "p-0.5 rounded-full",
                                        {
                                            " text-green-600": value.offer_from === "INTERNAL",
                                            " text-yellow-600": value.offer_from !== "INTERNAL",
                                        },
                                        "text-xs font-medium"
                                    )}
                                >
                                    <span>{value.offer_from}</span>
                                </div>
                            </div>
                        ),
                    },
                    {
                        name: "Reinsured",
                        accessor: "insurer.insurer_company_name",
                        render: (value) => (
                            <div className="flex flex-col text-xs">
                                <span className="font-medium">
                                    {value.insurer?.insurer_company_name || "N/A"}
                                </span>
                                <span className="font-light">
                                    {value.insurer?.insurer_company_email || "N/A"}
                                </span>
                            </div>
                        ),
                    },
                    {
                        name: "Insured",
                        accessor: "offer_detail.insured_by",
                        render: (value) => {
                            const details = JSON.parse(
                                value?.offer_detail?.offer_details || "[]"
                            );
                            return (
                                <div className="flex flex-col text-xs ">
                                    <span className="font-medium">
                                        {value.offer_detail?.insured_by || "N/A"}
                                    </span>
                                    <span className="font-light">
                                        {[
                                            "Motor Comprehensive Fleet",
                                            "Motor Comprehensive",
                                            "Motor Comprehensive (Automobile Fac Facility)",
                                        ].includes(value?.classofbusiness?.business_name || "")
                                            ? details?.find(
                                                (el: { [x: string]: string }) =>
                                                    el?.["keydetail"] === "Vehicle Reg No" ||
                                                    el?.["keydetail"] === "Vehicle Reg No."
                                            )?.value
                                            : ""}
                                    </span>
                                </div>
                            );
                        },
                    },
                    {
                        name: "Class Of Business",
                        accessor: "classofbusiness.business_name",
                        render: (value) => (
                            <div className="flex flex-col text-xs">
                                <span className="font-medium">
                                    {value?.classofbusiness?.business_name || "N/A"}
                                </span>
                            </div>
                        ),
                    },
                    {
                        name: "Sum Insured",
                        accessor: "sum_insured",
                        render: (value) => (
                            <div className="flex flex-col text-xs">
                                <span className="font-medium">
                                    {value?.offer_detail?.currency}{" "}
                                    {numeral(value.sum_insured).format("0,0 ") || "N/A"}
                                </span>
                            </div>
                        ),
                    },
                    {
                        name: "Rate",
                        accessor: "rate",
                    },
                    {
                        name: "Status",
                        accessor: "offer_status",
                        render: ({ offer_status }) => (
                            <span
                                className={clsx("text-xs", {
                                    "text-blue-600": offer_status === "OPEN",
                                    "text-yellow-600": offer_status === "PENDING",
                                    "text-teal-600": offer_status === "CLOSED",
                                })}
                            >
                                {offer_status}
                            </span>
                        ),
                    },
                    {
                        name: "Business State",
                        accessor: "business_state",
                    },
                    {
                        name: "Payment Type",
                        accessor: "payment_type",
                        render(data, index) {
                            return (
                                <span className="text-xs">
                                    {_.startCase(Object.keys(JSON.parse(data.offer_detail?.payment_type || "{}")).at(0) || "N/A")}
                                </span>
                            );
                        },
                    },
                    {
                        name: "Total Securities",
                        accessor: "total_securities",
                        render(data, index) {
                            return (
                                <span className="text-xs">
                                    {data?.offer_participant?.length || 0}
                                </span>
                            );
                        },
                    },
                    {
                        name: "Offer Date",
                        accessor: "created_at",
                        render: (value) => (
                            <span>{moment(value.created_at).format("ddd MMM D, YYYY")}</span>
                        ),
                    },
                    {
                        name: "Created By",
                        accessor: "employee.employee_first_name",
                        render: (value) => (
                            <div className="flex flex-row leading-0">
                                <span>
                                    {value?.employee?.employee_first_name}{" "}
                                    {value?.employee?.employee_last_name}
                                </span>
                            </div>
                        ),
                    },
                    {
                        name: "Actions",
                        accessor: "actions",
                        render: (value) => (
                            <>
                                <>
                                    <ActionButton
                                        action="view"
                                        tooltip="Debit & Cover Documents"
                                        onClick={dispatchAction(
                                            value.offer_id +
                                            ":" +
                                            value?.offer_from +
                                            ":" +
                                            value?.place_it_id,
                                            "view"
                                        )}
                                    />
                                    <ActionButton
                                        action="thumbs-up"
                                        tooltip="Approve Offer"
                                        onClick={dispatchAction(value?.offer_id, "accept")}
                                    />
                                    <ActionButton
                                        action="goto"
                                        tooltip="View Credit Notes"
                                        onClick={() => expandItem(value?.offer_id)}
                                    />
                                    {/* <ActionButton
                                        action="reject"
                                        tooltip="Reopen Offer"
                                        onClick={dispatchAction(value?.offer_id, "reject")}
                                    /> */}
                                    {withRoles([
                                        "CEO",
                                        "System Administrator",
                                        "Senior Broking Officer",
                                        "Finance Officer",
                                    ])(
                                        <ActionButton
                                            action="remove"
                                            tooltip="Cancel Offer"
                                            onClick={dispatchAction(value?.offer_id, "remove")}
                                        />
                                    )}
                                </>
                            </>
                        ),
                    },

                ]}
                data={offers}
                refetch={refetch}
                emptyMessage='No unapproved offers found.'
                pagination={() => <Paginator totalItems={count} currentSize={offers.length} offset={offset} limit={limit} setOffset={setPage} />}
            />

            {searchParams?.id && modal === "view" && (
                <ViewNotesContainer
                    open={modal === "view"}
                    setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
                />
            )}
            {searchParams?.id && modal === "remove" && (
                <RemoveContainer
                    open={modal === "remove"}
                    setOpen={(val: boolean) => setModal(val ? "remove" : undefined)}
                />
            )}
            {searchParams?.id && modal === "reject" && (
                <ReopenContainer
                    open={modal === "reject"}
                    setOpen={(val: boolean) => setModal(val ? "reject" : undefined)}
                />
            )}
            {searchParams?.id && modal === "accept" && (
                <ApproveOfferContainer
                    open={modal === "accept"}
                    setOpen={(val: boolean) => setModal(val ? "accept" : undefined)}
                />
            )}
        </>
    )
}

export default UnapprovedOffers